/* Admin · Users tab — Prep users + drill-down activity view.
   Lives at /admin, mounted from Admin.jsx. */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { extractErrorMessage, toastApiError } from "@/lib/apiError";
import {
  X, MagnifyingGlass, ArrowRight, Users as UsersIcon, ChartLineUp, FileText,
  PaperPlaneTilt, ListChecks, Eye, ChatCircleText,
  CheckCircle, Circle, Clock, Sparkle,
} from "@phosphor-icons/react";

const C = {
  bgDark: "#000",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  red: "#F87171",
  green: "#9AE6B4",
};

const KIND_META = {
  pitch: { label: "Pitch Deck", color: C.tealSoft, icon: FileText },
  model: { label: "Financial Model", color: C.orange, icon: ChartLineUp },
  mgmt:  { label: "Mgmt Accounts", color: C.green,    icon: ListChecks },
};

const FUNNEL_LABEL = {
  signed_up: "Signed up",
  building:  "Building",
  exported:  "Exported",
  shared:    "Shared",
};

const MGMT_STEP_LABEL = {
  collect:    "1 · Collect",
  clean:      "2 · Clean",
  reconcile:  "3 · Reconcile",
  classify:   "4 · Classify",
  pnl:        "5 · P&L",
  bs:         "6 · Balance sheet",
  cf:         "7 · Cashflow (Golden Rule)",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function UsersPanel() {
  const [funnel, setFunnel] = useState(null);
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null); // {kind, id}

  const refresh = () => {
    api.get(`/admin/users?limit=200&sort=${sort}${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then(({ data }) => setUsers(data))
      .catch(() => toast.error("Failed to load users"));
    api.get("/admin/users/funnel").then(({ data }) => setFunnel(data)).catch(() => {});
  };

  // Debounced auto-search when q changes
  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, q ? 280 : 0);
    return () => clearTimeout(t);
  }, [q, sort]);

  return (
    <section data-testid="admin-users-panel" className="space-y-6">
      {/* Funnel strip */}
      <FunnelStrip data={funnel} />

      {/* Search + sort */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && refresh()}
            placeholder="Search by name or email…" data-testid="admin-users-search"
            className="w-full bg-black/40 border border-white/10 focus:border-white/30 rounded-md pl-9 pr-3 py-2 text-sm text-white outline-none transition-colors" />
        </div>
        <div className="flex items-center gap-2">
          {[["recent", "Last active"], ["new", "Newest"], ["busiest", "Most active"], ["name", "Name"]].map(([id, label]) => (
            <button key={id} onClick={() => setSort(id)} data-testid={`admin-users-sort-${id}`}
              className="px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.15em] transition-colors"
              style={{
                backgroundColor: sort === id ? C.teal : "rgba(0,0,0,0.35)",
                color: sort === id ? "#fff" : "rgba(255,255,255,0.55)",
                border: `1px solid ${sort === id ? C.teal : "rgba(255,255,255,0.12)"}`,
              }}>{label}</button>
          ))}
          <button onClick={refresh} data-testid="admin-users-refresh"
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.65)" }}>Refresh</button>
        </div>
      </div>

      {/* Table */}
      {!users ? (
        <div className="text-white/45 text-sm">Loading users…</div>
      ) : users.rows.length === 0 ? (
        <div className="rounded-xl border px-6 py-12 text-center text-white/40 text-sm"
          style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.2)" }} data-testid="admin-users-empty">
          No users match this filter.
        </div>
      ) : (
        <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/45 uppercase tracking-wider text-[10px]" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Currently working on</th>
                <th className="px-4 py-3 text-right">Docs</th>
                <th className="px-4 py-3">Funnel</th>
                <th className="px-4 py-3">Last active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.rows.map((u, i) => (
                <UserRow key={u.user_id} u={u} idx={i} onOpen={() => setSelectedUserId(u.user_id)} />
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 text-[11px] text-white/35 border-t border-white/5">
            Showing {users.rows.length} of {users.total} users
          </div>
        </div>
      )}

      {selectedUserId && (
        <UserDrawer
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onOpenDoc={(kind, id) => setViewingDoc({ kind, id })}
        />
      )}
      {viewingDoc && (
        <DocViewer doc={viewingDoc} onClose={() => setViewingDoc(null)} />
      )}
    </section>
  );
}

function FunnelStrip({ data }) {
  if (!data) return <div className="text-white/40 text-sm">Loading funnel…</div>;
  const steps = [
    { k: "signed_up",       label: "Signed up",       v: data.signed_up,        color: C.tealSoft, icon: UsersIcon },
    { k: "started_any_doc", label: "Started a doc",   v: data.started_any_doc,  color: C.teal,     icon: Sparkle },
    { k: "started_pitch",   label: "Pitch decks",     v: data.started_pitch,    color: C.tealSoft, icon: FileText },
    { k: "started_model",   label: "Financial models",v: data.started_model,    color: C.orange,   icon: ChartLineUp },
    { k: "started_mgmt",    label: "Mgmt accounts",   v: data.started_mgmt,     color: C.green,    icon: ListChecks },
    { k: "exported_pitch",  label: "Exported deck",   v: data.exported_pitch || 0, color: C.orange, icon: PaperPlaneTilt },
    { k: "active_7d",       label: "Active (7d)",     v: data.active_7d,        color: C.orange,   icon: Clock },
    { k: "shared_pitch",    label: "Shared a deck",   v: data.shared_pitch,     color: C.green,    icon: PaperPlaneTilt },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="admin-users-funnel">
      {steps.map((s) => {
        const Icon = s.icon;
        const pct = data.signed_up ? Math.round((s.v / data.signed_up) * 100) : 0;
        return (
          <div key={s.k} className="rounded-xl border p-3"
            style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.3)" }}
            data-testid={`admin-funnel-${s.k}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">
              <Icon size={11} weight="fill" style={{ color: s.color }} />
              {s.label}
            </div>
            <div className="text-2xl font-extrabold font-mono-num mt-1.5" style={{ color: s.color, letterSpacing: "-0.02em" }}>
              {s.v}
            </div>
            {s.k !== "signed_up" && (
              <div className="text-[10px] text-white/35 mt-0.5">{pct}% of signups</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UserRow({ u, idx, onOpen }) {
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
      onClick={onOpen} data-testid={`admin-user-row-${idx}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-white text-[13px]">{u.name || "(no name)"}</div>
        <div className="text-[11px] text-white/45 mt-0.5 font-mono-num">{u.email}</div>
        {u.is_admin && (
          <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${C.orange}22`, color: C.orange }}>Admin</span>
        )}
        {u.email_verified === false && (
          <span className="inline-block mt-1 ml-1 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: "rgba(239,68,68,0.14)", color: "#F87171" }}
            data-testid={`admin-user-unverified-${idx}`}>Unverified</span>
        )}
      </td>
      <td className="px-4 py-3 max-w-[260px]">
        {u.focus ? <FocusPill focus={u.focus} /> : <span className="text-white/30 text-xs">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        <DocCounts c={u.counts} />
      </td>
      <td className="px-4 py-3">
        <FunnelBadge stage={u.funnel_stage} />
      </td>
      <td className="px-4 py-3 text-white/55 text-xs">{timeAgo(u.last_active)}</td>
      <td className="px-4 py-3 text-right">
        <ArrowRight size={14} className="text-white/30 inline-block" />
      </td>
    </tr>
  );
}

function FocusPill({ focus }) {
  const meta = KIND_META[focus.kind] || { label: focus.kind, color: C.tealSoft };
  const Icon = meta.icon || FileText;
  return (
    <div className="flex items-start gap-2">
      <Icon size={12} weight="fill" style={{ color: meta.color }} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-white/85 truncate" title={focus.name}>{focus.name}</div>
        <div className="text-[10px] text-white/45 mt-0.5">
          {focus.kind === "pitch" && focus.next_step
            ? `Slide ${focus.next_step.num} · ${focus.next_step.title}`
            : focus.kind === "mgmt" && focus.next_step
            ? `Stuck on ${MGMT_STEP_LABEL[focus.next_step] || focus.next_step}`
            : focus.status}
          {typeof focus.completion_pct === "number" && <span className="ml-1 text-white/35">· {focus.completion_pct}%</span>}
        </div>
      </div>
    </div>
  );
}

function DocCounts({ c }) {
  if (!c?.total) return <span className="text-white/30 text-xs">0</span>;
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] font-mono-num">
      {c.pitch_decks > 0 && <Tag count={c.pitch_decks} color={C.tealSoft} title="Pitch decks" />}
      {c.models > 0 && <Tag count={c.models} color={C.orange} title="Financial models" />}
      {c.mgmt_accounts > 0 && <Tag count={c.mgmt_accounts} color={C.green} title="Mgmt accounts" />}
    </div>
  );
}

function Tag({ count, color, title }) {
  return (
    <span title={title} className="px-1.5 py-0.5 rounded font-bold"
      style={{ backgroundColor: `${color}22`, color: color }}>{count}</span>
  );
}

function FunnelBadge({ stage }) {
  const palette = {
    signed_up: { bg: "rgba(255,255,255,0.06)", fg: "rgba(255,255,255,0.55)" },
    building:  { bg: `${C.tealSoft}22`, fg: C.tealSoft },
    exported:  { bg: `${C.orange}22`,   fg: C.orange },
    shared:    { bg: `${C.green}22`,    fg: C.green },
  }[stage] || { bg: "rgba(255,255,255,0.06)", fg: "rgba(255,255,255,0.5)" };
  return (
    <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded"
      style={{ backgroundColor: palette.bg, color: palette.fg }}>{FUNNEL_LABEL[stage] || stage}</span>
  );
}

// ============================================================================
// User drawer
// ============================================================================

function UserDrawer({ userId, onClose, onOpenDoc }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("docs");
  const [toggling, setToggling] = useState(false);
  const [granting, setGranting] = useState(false);
  const [loggingSession, setLoggingSession] = useState(false);

  useEffect(() => {
    api.get(`/admin/users/${userId}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load user"));
  }, [userId]);

  const deleteUser = async () => {
    if (!data) return;
    const email = data.user.email || "";
    const typed = window.prompt(
      `This will permanently delete ${email} along with ALL of their pitch decks, financial models, management accounts, valuations, credits, and audit history. This cannot be undone.\n\nTo confirm, type the user's email exactly:`,
      ""
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== email.trim().toLowerCase()) {
      toast.error("Email didn't match — nothing was deleted.");
      return;
    }
    setToggling(true);
    try {
      const { data: res } = await api.delete(`/admin/users/${userId}`, {
        data: { confirm_email: email },
      });
      const counts = res.cascade_counts || {};
      const summary = Object.entries(counts)
        .map(([k, v]) => `${v} ${k.replace("_", " ")}`)
        .join(" · ") || "no related records";
      toast.success(`Deleted ${email} (${summary})`);
      onClose();
    } catch (e) {
      toastApiError(e, "Could not delete user.", { context: { action: "admin_delete_user", user_id: userId } });
    } finally { setToggling(false); }
  };

  const grantCredits = async () => {
    if (!data) return;
    const email = data.user.email || "";
    const raw = window.prompt(
      `Grant free credits to ${email}.\n\nEnter the number of credits to add (1–10000):`,
      "100"
    );
    if (raw === null) return;
    const amount = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(amount) || amount <= 0 || amount > 10000) {
      toast.error("Enter a positive whole number between 1 and 10000.");
      return;
    }
    const reason = window.prompt(
      `Optional — short note explaining why these credits are being granted (logged in the audit trail). Leave blank to skip.`,
      ""
    );
    if (reason === null) return; // admin hit Cancel
    setGranting(true);
    try {
      const { data: res } = await api.post(`/admin/users/${userId}/credits/grant`, {
        amount,
        reason: (reason || "").trim(),
      });
      toast.success(`+${res.amount_granted} credits → ${res.email} · new balance ${res.new_balance}`);
      // Reflect the new balance in the drawer immediately
      setData((d) => d ? { ...d, user: { ...d.user, credits_balance: res.new_balance } } : d);
    } catch (e) {
      toastApiError(e, "Could not grant credits.", {
        context: { action: "admin_grant_credits", user_id: userId, amount },
      });
    } finally { setGranting(false); }
  };

  const logAdvisorSession = async () => {
    if (!data) return;
    const email = data.user.email || "";
    const SERVICE_OPTS = [
      ["pitch_deck", "Pitch Deck"],
      ["financial_model", "Financial Model"],
      ["mgmt_accounts", "Management Accounts"],
      ["business_valuation", "Business Valuation"],
      ["general", "General"],
    ];
    const sList = SERVICE_OPTS.map(([k, l], i) => `${i + 1}. ${l}`).join("\n");
    const sRaw = window.prompt(
      `Log an advisor session for ${email} (1st = 30 min free, follow-ons = 75 min · KES 12,000).\n\nWhich service? Type the number:\n${sList}`,
      "1"
    );
    if (sRaw === null) return;
    const idx = parseInt(String(sRaw).trim(), 10);
    if (!Number.isFinite(idx) || idx < 1 || idx > SERVICE_OPTS.length) {
      toast.error("Pick a number 1–5.");
      return;
    }
    const service = SERVICE_OPTS[idx - 1][0];
    const notes = window.prompt(
      `Optional — short session summary the founder will receive in their email (max 600 chars). Leave blank to skip.`,
      ""
    );
    if (notes === null) return;
    setLoggingSession(true);
    try {
      const { data: res } = await api.post(`/advisor/sessions/complete`, {
        user_id: data.user.user_id,
        user_email: email,
        user_name: data.user.name || "",
        service,
        notes: (notes || "").trim(),
      });
      const s = res.session;
      if (s.is_free) {
        toast.success(`Session logged · 1st session was FREE. Thank-you email sent to ${email}.`,
          { duration: 8000 });
      } else {
        toast.success(`Session logged · invoice for KES ${(s.amount_kes ?? 0).toLocaleString()} sent to ${email}.`,
          { duration: 9000 });
      }
    } catch (e) {
      toastApiError(e, "Could not log session.", {
        context: { action: "advisor_session_complete", user_id: data.user.user_id, service },
      });
    } finally { setLoggingSession(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-user-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-3xl h-full overflow-y-auto border-l"
        style={{ backgroundColor: "#0A1614", borderColor: "rgba(123, 211, 206, 0.18)" }}>
        <header className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "#0A1614" }}>
          <div className="min-w-0">
            {!data ? <div className="text-white/45 text-sm">Loading…</div> : (
              <>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: C.orange }}>Prep user</div>
                <h3 className="text-xl font-extrabold tracking-tight mt-0.5 text-white truncate" style={{ letterSpacing: "-0.02em" }}>
                  {data.user.name || "(no name)"}
                </h3>
                <div className="text-[12px] text-white/55 font-mono-num mt-0.5 truncate">{data.user.email}</div>
              </>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="admin-user-drawer-close"
            className="text-white/55 hover:text-white p-1.5 rounded transition-colors">
            <X size={18} />
          </button>
        </header>

        {!data ? null : (
          <>
            {/* Profile meta */}
            <div className="px-6 py-4 border-b grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs"
              style={{ borderColor: "rgba(123, 211, 206, 0.08)" }}>
              <Meta label="Signed up" v={data.user.created_at ? new Date(data.user.created_at).toLocaleDateString() : "—"} />
              <Meta label="Auth provider" v={data.user.auth_provider || "email"} />
              <div data-testid="admin-user-email-verified">
                <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/40">Email verified</div>
                <div className="mt-1 text-white/80">
                  {data.user.email_verified === false ? (
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded"
                      style={{ backgroundColor: "rgba(239,68,68,0.14)", color: "#F87171" }}>Pending</span>
                  ) : (
                    <>
                      <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded"
                        style={{ backgroundColor: "rgba(34,197,94,0.14)", color: "#4ADE80" }}>Verified</span>
                      <span className="ml-2 text-[11px] text-white/45">
                        {data.user.email_verified_at
                          ? new Date(data.user.email_verified_at).toLocaleDateString()
                          : data.user.verified_via === "grandfathered" ? "pre-verification era" : ""}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/40">Role</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: data.user.is_admin ? `${C.orange}22` : "rgba(255,255,255,0.06)",
                      color: data.user.is_admin ? C.orange : "rgba(255,255,255,0.55)",
                    }}
                    data-testid="admin-user-role-badge">
                    {data.user.is_admin ? "Admin" : "Member"}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded inline-flex items-center gap-1"
                    style={{ backgroundColor: `${C.tealSoft}1A`, color: C.tealSoft }}
                    data-testid="admin-user-credits-balance">
                    <Sparkle size={10} weight="fill" />
                    {Number(data.user.credits_balance ?? 0).toLocaleString()} credits
                  </span>
                  <button onClick={grantCredits} disabled={granting}
                    data-testid="admin-grant-credits"
                    className="text-[10px] font-semibold px-2 py-1 rounded transition-colors disabled:opacity-50 hover:bg-[#0A8B85]/10"
                    style={{ border: `1px solid ${C.teal}80`, color: C.teal }}
                    title="Add free credits to this user's account. Stored as a fresh credit bucket; logged in the audit trail.">
                    {granting ? "Granting…" : "Grant credits"}
                  </button>
                  <button onClick={logAdvisorSession} disabled={loggingSession}
                    data-testid="admin-log-advisor-session"
                    className="text-[10px] font-semibold px-2 py-1 rounded transition-colors disabled:opacity-50 hover:bg-[#F59E0B]/10"
                    style={{ border: `1px solid ${C.orange}80`, color: C.orange }}
                    title="Mark a completed advisor session. 1st session is 30 min & free; follow-ons are 75 min & trigger a KES 12,000 Paystack invoice email.">
                    {loggingSession ? "Logging…" : "Log advisor session"}
                  </button>
                  {!data.user.is_admin && (
                    <button onClick={deleteUser} disabled={toggling}
                      data-testid="admin-delete-user"
                      className="text-[10px] font-semibold px-2 py-1 rounded transition-colors disabled:opacity-50"
                      style={{
                        border: `1px solid ${C.red}55`,
                        color: C.red,
                      }}>
                      {toggling ? "Deleting…" : "Delete user"}
                    </button>
                  )}
                </div>
              </div>
              {data.user.utm_source && <Meta label="UTM source" v={data.user.utm_source} />}
              {data.user.utm_campaign && <Meta label="UTM campaign" v={data.user.utm_campaign} />}
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 flex gap-2 border-b" style={{ borderColor: "rgba(123, 211, 206, 0.08)" }}>
              {[["docs", `Documents (${data.pitch_decks.length + data.models.length + data.mgmt_accounts.length})`],
                ["activity", `Activity (${(data.activity || []).length})`]].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)}
                  data-testid={`admin-user-tab-${id}`}
                  className="text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-2 rounded-t-md transition-colors"
                  style={{
                    color: tab === id ? "#fff" : "rgba(255,255,255,0.45)",
                    borderBottom: tab === id ? `2px solid ${C.teal}` : "2px solid transparent",
                  }}>{label}</button>
              ))}
            </div>

            <div className="px-6 py-5">
              {tab === "docs" ? (
                <UserDocsTab data={data} onOpenDoc={onOpenDoc} />
              ) : (
                <UserActivityTab activity={data.activity || []} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Meta({ label, v }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/40">{label}</div>
      <div className="text-white/80 text-[12px] mt-0.5 truncate">{v}</div>
    </div>
  );
}

function UserDocsTab({ data, onOpenDoc }) {
  const empty = data.pitch_decks.length + data.models.length + data.mgmt_accounts.length === 0;
  if (empty) {
    return (
      <div className="text-center py-10 text-white/40 text-sm" data-testid="admin-user-docs-empty">
        This user hasn&apos;t started a document yet.
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="admin-user-docs">
      {data.pitch_decks.length > 0 && (
        <DocGroup label="Pitch decks" color={C.tealSoft}>
          {data.pitch_decks.map((d) => (
            <PitchCard key={d.deck_id} d={d} onView={() => onOpenDoc("pitch", d.deck_id)} />
          ))}
        </DocGroup>
      )}
      {data.models.length > 0 && (
        <DocGroup label="Financial models" color={C.orange}>
          {data.models.map((m) => (
            <ModelCard key={m.model_id} m={m} onView={() => onOpenDoc("model", m.model_id)} />
          ))}
        </DocGroup>
      )}
      {data.mgmt_accounts.length > 0 && (
        <DocGroup label="Management accounts" color={C.green}>
          {data.mgmt_accounts.map((a) => (
            <MgmtCard key={a.account_id} a={a} onView={() => onOpenDoc("mgmt", a.account_id)} />
          ))}
        </DocGroup>
      )}
    </div>
  );
}

function DocGroup({ label, color, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2 flex items-center gap-1.5"
        style={{ color }}>
        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ProgressBar({ pct, color }) {
  return (
    <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color }} />
    </div>
  );
}

function PitchCard({ d, onView }) {
  return (
    <div className="rounded-lg border px-3.5 py-3" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }} data-testid={`admin-doc-pitch-${d.deck_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-white text-[13px] truncate">{d.company_name || "Untitled"}</div>
          <div className="text-[11px] text-white/45 mt-0.5 truncate">
            {d.industry || "—"} · {d.stage || "—"} · {d.funder_type || "—"}
          </div>
          {d.one_liner && <div className="text-[12px] text-white/65 mt-1.5 italic line-clamp-2">&ldquo;{d.one_liner}&rdquo;</div>}
        </div>
        <button onClick={onView} className="text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1.5 shrink-0"
          style={{ border: "1px solid rgba(123,211,206,0.25)", color: C.tealSoft }} data-testid={`admin-view-pitch-${d.deck_id}`}>
          <Eye size={12} /> View
        </button>
      </div>
      <div className="mt-2.5">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-white/45">{d.filled}/{d.total} slides drafted</span>
          <span className="font-mono-num font-bold" style={{ color: C.tealSoft }}>{d.completion_pct}%</span>
        </div>
        <ProgressBar pct={d.completion_pct} color={C.tealSoft} />
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-white/45 flex-wrap">
        {d.next_step ? (
          <span>Next: <strong className="text-white/70">Slide {d.next_step.num} — {d.next_step.title}</strong></span>
        ) : <span style={{ color: C.green }}>All slides drafted ✓</span>}
        {d.is_public && <span style={{ color: C.green }}>· Shared publicly</span>}
        <span className="ml-auto">{timeAgo(d.updated_at)}</span>
      </div>
    </div>
  );
}

function ModelCard({ m, onView }) {
  const stage = m.profile?.stage || "—";
  return (
    <div className="rounded-lg border px-3.5 py-3" style={{ borderColor: "rgba(245, 158, 11, 0.18)" }} data-testid={`admin-doc-model-${m.model_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-white text-[13px] truncate">{m.name || "Untitled"}</div>
          <div className="text-[11px] text-white/45 mt-0.5">
            {m.profile?.sector || "—"} · {stage} · {m.profile?.funder_type || "—"} · {m.profile?.currency || ""}
          </div>
        </div>
        <button onClick={onView} className="text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1.5 shrink-0"
          style={{ border: "1px solid rgba(245,158,11,0.3)", color: C.orange }} data-testid={`admin-view-model-${m.model_id}`}>
          <Eye size={12} /> View
        </button>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-white/45 flex-wrap">
        <span>{m.sections_with_data} assumption section{m.sections_with_data === 1 ? "" : "s"} filled</span>
        <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-[0.15em]"
          style={{ backgroundColor: `${C.orange}22`, color: C.orange, fontSize: "9px" }}>{m.status}</span>
        <span className="ml-auto">{timeAgo(m.updated_at)}</span>
      </div>
    </div>
  );
}

function MgmtCard({ a, onView }) {
  return (
    <div className="rounded-lg border px-3.5 py-3" style={{ borderColor: "rgba(154, 230, 180, 0.18)" }} data-testid={`admin-doc-mgmt-${a.account_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-white text-[13px] truncate">{a.company_name || "Untitled"}</div>
          <div className="text-[11px] text-white/45 mt-0.5">
            {a.target_month} · {a.industry || "—"} · {a.transactions} txns
          </div>
        </div>
        <button onClick={onView} className="text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1.5 shrink-0"
          style={{ border: "1px solid rgba(154,230,180,0.3)", color: C.green }} data-testid={`admin-view-mgmt-${a.account_id}`}>
          <Eye size={12} /> View
        </button>
      </div>
      <div className="mt-2.5">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-white/45">{a.steps_passed}/{a.total_steps} gates passed</span>
          <span className="font-mono-num font-bold" style={{ color: C.green }}>{a.completion_pct}%</span>
        </div>
        <ProgressBar pct={a.completion_pct} color={C.green} />
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-white/45 flex-wrap">
        {a.next_step ? (
          <span>Stuck on: <strong className="text-white/70">{MGMT_STEP_LABEL[a.next_step] || a.next_step}</strong></span>
        ) : <span style={{ color: C.green }}>All 7 gates passed ✓</span>}
        <span className="ml-auto">{timeAgo(a.updated_at)}</span>
      </div>
    </div>
  );
}

function UserActivityTab({ activity }) {
  if (activity.length === 0) {
    return <div className="text-center py-10 text-white/40 text-sm" data-testid="admin-user-activity-empty">No activity recorded yet.</div>;
  }
  return (
    <ol className="space-y-2" data-testid="admin-user-activity">
      {activity.map((a) => (
        <li key={a.id} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-b-0">
          <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded mt-0.5 shrink-0"
            style={{
              backgroundColor: a.level === "error" ? "rgba(248,113,113,0.15)" : (a.level === "warn" ? "rgba(245,158,11,0.15)" : "rgba(10,139,133,0.18)"),
              color: a.level === "error" ? C.red : (a.level === "warn" ? C.orange : C.tealSoft),
            }}>{a.level}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-white/85 font-semibold">{a.event}</div>
            {a.message && <div className="text-[11px] text-white/55 mt-0.5 break-words">{a.message}</div>}
            {a.entity_type && <div className="text-[10px] text-white/35 mt-0.5 font-mono-num">{a.entity_type}/{(a.entity_id || "").slice(0, 16)}…</div>}
          </div>
          <div className="text-[10px] text-white/35 font-mono-num shrink-0">{timeAgo(a.created_at)}</div>
        </li>
      ))}
    </ol>
  );
}

// ============================================================================
// Read-only doc viewer
// ============================================================================

function DocViewer({ doc, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/admin/docs/${doc.kind}/${doc.id}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(extractErrorMessage(e, "Failed to load document")));
  }, [doc]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="admin-doc-viewer">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border"
        style={{ backgroundColor: "#0A1614", borderColor: "rgba(123, 211, 206, 0.2)" }}>
        <header className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "#0A1614" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: C.orange }}>
              Read-only · advisor view
            </div>
            <h3 className="text-lg font-extrabold tracking-tight mt-0.5 text-white">
              {KIND_META[doc.kind]?.label || doc.kind}
            </h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="admin-doc-viewer-close"
            className="text-white/55 hover:text-white p-1.5 rounded">
            <X size={18} />
          </button>
        </header>
        <div className="px-6 py-5">
          {err ? <div className="text-red-400 text-sm">{err}</div>
            : !data ? <div className="text-white/45 text-sm">Loading…</div>
            : doc.kind === "pitch" ? <PitchView data={data} />
            : doc.kind === "model" ? <ModelView data={data} />
            : <MgmtView data={data} />}
        </div>
      </div>
    </div>
  );
}

function PitchView({ data }) {
  const d = data.deck;
  return (
    <div className="space-y-5" data-testid="admin-pitch-view">
      <ViewHeader title={d.company_name} subtitle={d.one_liner} owner={data.owner} progress={data.progress} color={C.tealSoft} />
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Meta label="Industry" v={d.industry} />
        <Meta label="Stage" v={d.stage} />
        <Meta label="Funder type" v={d.funder_type} />
        <Meta label="Country" v={d.country} />
        <Meta label="Capital ask" v={d.capital_ask} />
        <Meta label="Revenue model" v={d.revenue_model} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55 mb-3">Slides</div>
        <ol className="space-y-3">
          {(d.slides || []).map((s) => (
            <li key={s.num} className="rounded-lg border px-4 py-3" style={{ borderColor: "rgba(123, 211, 206, 0.1)" }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded font-mono-num"
                  style={{ backgroundColor: `${C.teal}22`, color: C.tealSoft }}>{String(s.num).padStart(2, "0")}</span>
                <span className="text-[13px] font-bold text-white">{s.title}</span>
                {s.content?.trim() ? <CheckCircle size={12} weight="fill" style={{ color: C.green }} /> : <Circle size={12} style={{ color: "rgba(255,255,255,0.25)" }} />}
              </div>
              {s.question && <div className="text-[11px] text-white/45 italic mb-1.5">{s.question}</div>}
              <div className="text-[12px] text-white/75 whitespace-pre-wrap leading-relaxed">
                {s.content?.trim() ? s.content : <span className="text-white/30 italic">Not drafted yet.</span>}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function ModelView({ data }) {
  const m = data.model;
  const profile = m.profile || {};
  const assumptions = m.assumptions || {};
  return (
    <div className="space-y-5" data-testid="admin-model-view">
      <ViewHeader title={m.name} subtitle={`${profile.sector || ""} · ${profile.stage || ""}`} owner={data.owner} color={C.orange} />
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">Profile</div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {Object.entries(profile).map(([k, v]) => (
            <Meta key={k} label={k.replace(/_/g, " ")} v={String(v ?? "—")} />
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">Assumption sections</div>
        {Object.keys(assumptions).length === 0 ? (
          <div className="text-white/40 text-xs italic">No assumptions filled in yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {Object.entries(assumptions).map(([k, v]) => {
              const size = Array.isArray(v) ? v.length : (typeof v === "object" && v ? Object.keys(v).length : (v ? 1 : 0));
              return (
                <li key={k} className="flex items-center justify-between text-[12px] py-1.5 border-b border-white/5">
                  <span className="text-white/80 capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="text-white/45 font-mono-num">{size} entr{size === 1 ? "y" : "ies"}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function MgmtView({ data }) {
  const a = data.account;
  const gates = a.gates || {};
  const opening = a.opening || {};
  const closing = a.closing || {};
  const STEPS = ["collect", "clean", "reconcile", "classify", "pnl", "bs", "cf"];
  return (
    <div className="space-y-5" data-testid="admin-mgmt-view">
      <ViewHeader title={a.company_name} subtitle={`${a.target_month} · ${a.industry || ""}`} owner={data.owner} progress={data.progress} color={C.green} />
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">7-step pipeline</div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {STEPS.map((s) => (
            <li key={s} className="flex items-center gap-2 text-[12px] px-3 py-2 rounded-lg border"
              style={{ borderColor: gates[s] ? "rgba(154,230,180,0.25)" : "rgba(255,255,255,0.06)", backgroundColor: gates[s] ? "rgba(154,230,180,0.05)" : "transparent" }}>
              {gates[s] ? <CheckCircle size={14} weight="fill" style={{ color: C.green }} /> : <Circle size={14} style={{ color: "rgba(255,255,255,0.25)" }} />}
              <span className={gates[s] ? "text-white/85" : "text-white/50"}>{MGMT_STEP_LABEL[s]}</span>
            </li>
          ))}
        </ol>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">Balances (KES)</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <BalanceTile label="Opening bank" v={opening.opening_bank} />
          <BalanceTile label="Opening M-Pesa" v={opening.opening_mpesa} />
          <BalanceTile label="Opening cash" v={opening.opening_cash} />
          <BalanceTile label="Closing bank" v={closing.closing_bank} />
          <BalanceTile label="Closing M-Pesa" v={closing.closing_mpesa} />
          <BalanceTile label="Closing cash" v={closing.closing_cash} />
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">
          Transactions ({(a.transactions || []).length})
        </div>
        <div className="text-[11px] text-white/45">
          Source mix: XL {(a.transactions || []).filter(t => t.source === "XL").length} ·
          BNK {(a.transactions || []).filter(t => t.source === "BNK").length} ·
          MPS {(a.transactions || []).filter(t => t.source === "MPS").length}
        </div>
      </div>
    </div>
  );
}

function BalanceTile({ label, v }) {
  return (
    <div className="rounded border px-3 py-2" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-white/40">{label}</div>
      <div className="font-mono-num text-white text-[13px] mt-0.5">
        {typeof v === "number" ? v.toLocaleString() : "—"}
      </div>
    </div>
  );
}

function ViewHeader({ title, subtitle, owner, progress, color }) {
  return (
    <div className="border-b pb-4" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
      <h4 className="text-xl font-extrabold tracking-tight text-white" style={{ letterSpacing: "-0.02em" }}>{title || "Untitled"}</h4>
      {subtitle && <div className="text-[12px] text-white/55 mt-0.5">{subtitle}</div>}
      <div className="flex items-center gap-4 mt-2 text-[11px] text-white/55">
        {owner && <span>Owner: <strong className="text-white/80">{owner.name || owner.email}</strong></span>}
        {progress && typeof progress.completion_pct === "number" && (
          <span>Completion: <strong style={{ color }}>{progress.completion_pct}%</strong></span>
        )}
      </div>
    </div>
  );
}
